// This is a separate file since the other utils import `config.js`, which depends on `window`
// and cannot be used in the service worker

import emojisMapped from "./emojisMapped";
import type { Notification, Attachment } from "./types";

const toEmojis = (tags: string[] | undefined) => {
  if (!tags) return [];
  return tags.filter((tag) => tag in emojisMapped).map((tag) => emojisMapped[tag]);
};

export const formatTitle = (m: Notification) => {
  const emojiList = toEmojis(m.tags);
  if (emojiList.length > 0) {
    return `${emojiList.join(" ")} ${m.title}`;
  }
  return m.title;
};

const formatTitleWithDefault = (m: Notification, fallback: string): string => {
  if (m.title) {
    // m.title is truthy here, so formatTitle never returns undefined; `?? fallback`
    // is a no-op at runtime and only narrows the type for TS.
    return formatTitle(m) ?? fallback;
  }
  return fallback;
};

export const formatMessage = (m: Notification) => {
  if (m.title) {
    return m.message || "";
  }
  const emojiList = toEmojis(m.tags);
  if (emojiList.length > 0) {
    return `${emojiList.join(" ")} ${m.message || ""}`;
  }
  return m.message || "";
};

export const imageRegex = /\.(png|jpe?g|gif|webp)$/i;
export const isImage = (attachment: Attachment | undefined) => {
  if (!attachment) return false;

  // if there's a type, only take that into account
  if (attachment.type) {
    return attachment.type.startsWith("image/");
  }

  // otherwise, check the extension
  return attachment.name?.match(imageRegex) || attachment.url?.match(imageRegex);
};

/** 메시지의 첨부 전부 — /api/v2 행은 attachments[], 옛 행·앱이 넘긴 알림은 attachment 하나 */
export const notificationAttachments = (m: Notification): Attachment[] => {
  if (m.attachments && m.attachments.length > 0) {
    return m.attachments;
  }
  return m.attachment ? [m.attachment] : [];
};

export const icon = "/static/images/ntfy.png";
export const badge = "/static/images/mask-icon.svg";

/**
 * Computes a unique notification tag scoped by baseUrl, topic, and sequence ID.
 * This ensures notifications from different topics with the same sequence ID don't collide.
 */
export const notificationTag = (baseUrl: string, topic: string, sequenceId: string) => `${baseUrl}/${topic}/${sequenceId}`;

interface ToNotificationParamsArgs {
  message: Notification;
  defaultTitle: string;
  topicRoute: string;
  baseUrl: string;
  topic: string;
}

/** Extends the standard NotificationOptions with fields the browser/SW supports but lib.dom omits. */
interface ShowNotificationOptions extends NotificationOptions {
  image?: string;
  timestamp?: number;
  renotify?: boolean;
  actions?: { action: string; title: string }[];
}

export const toNotificationParams = ({
  message,
  defaultTitle,
  topicRoute,
  baseUrl,
  topic,
}: ToNotificationParamsArgs): [string, ShowNotificationOptions] => {
  const firstImage = notificationAttachments(message).find((a) => isImage(a));
  const image = firstImage ? firstImage.thumb || firstImage.url : undefined;
  const sequenceId = message.sequence_id || message.id;
  const tag = notificationTag(baseUrl, topic, sequenceId);
  const subscriptionId = `${baseUrl}/${topic}`;

  // https://developer.mozilla.org/en-US/docs/Web/API/Notifications_API
  return [
    formatTitleWithDefault(message, defaultTitle),
    {
      body: formatMessage(message),
      badge,
      icon,
      image,
      timestamp: message.time * 1000,
      tag, // Scoped by baseUrl/topic/sequenceId to avoid cross-topic collisions
      renotify: true,
      silent: false,
      // This is used by the notification onclick event
      data: {
        subscriptionId,
        message,
        topicRoute,
      },
      actions: message.actions
        ?.filter(({ action, url }) => action === "view" && url)
        .map(({ label }) => ({
          action: label,
          title: label,
        })),
    },
  ];
};

export const messageWithSequenceId = (message: Notification): Notification => {
  if (message.sequenceId) {
    return message;
  }
  return { ...message, sequenceId: message.sequence_id || message.id };
};
